/**
 * Parser polskiego inputu naturalnego w strukturę task.
 *
 * Wykrywa:
 * - task_type z czasowników (podlej/zbierz/oprysk)
 * - due_date z fraz czasowych (dziś/jutro/pojutrze/sobota/za 3 dni)
 * - bed_id z nazwy rośliny dopasowanej do listy beds
 *
 * Output: { task_type, due_date (ISO), bed_id, description, parsed: {...} }
 *         parsed ma flagi: hasType, hasDate, hasPlant - do wyświetlenia w UI
 */

const TYPE_PATTERNS = [
  { type: 'water',   re: /\b(podl(ej|ewa|ej[au])|polej|nawodnij|zalej)\b/i, label: 'Podlewanie' },
  { type: 'harvest', re: /\b(zbier(z|aj|am|asz)|zbi[óo]r|wykop(uj)?|tn(ij|iesz))\b/i, label: 'Zbiór' },
  { type: 'spray',   re: /\b(opryskaj|opry?sk|spry?skaj|nawiez|nawo[zż]e?)\b/i, label: 'Oprysk' },
];

const DAY_NAMES = {
  poniedzialek: 1, poniedziałek: 1, pon: 1,
  wtorek: 2, wt: 2,
  sroda: 3, środa: 3, sr: 3, śr: 3,
  czwartek: 4, czw: 4,
  piatek: 5, piątek: 5, pt: 5,
  sobota: 6, sob: 6, so: 6,
  niedziela: 0, niedz: 0, nd: 0, ndz: 0,
};

function isoDate(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function nextDayOfWeek(targetDayOfWeek, fromDate = new Date()) {
  const result = new Date(fromDate);
  result.setHours(0, 0, 0, 0);
  const current = result.getDay();
  let diff = targetDayOfWeek - current;
  if (diff <= 0) diff += 7;
  result.setDate(result.getDate() + diff);
  return result;
}

function parseDate(input) {
  const lower = input.toLowerCase();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // dzisiaj/dziś
  if (/\b(dzi[śs](iaj)?|teraz)\b/.test(lower)) {
    return { date: isoDate(today), label: 'dziś' };
  }
  // jutro
  if (/\bjutro\b/.test(lower)) {
    const t = new Date(today); t.setDate(t.getDate() + 1);
    return { date: isoDate(t), label: 'jutro' };
  }
  // pojutrze
  if (/\bpojutrze\b/.test(lower)) {
    const t = new Date(today); t.setDate(t.getDate() + 2);
    return { date: isoDate(t), label: 'pojutrze' };
  }
  // weekend
  if (/\bweekend(em|u)?\b/.test(lower)) {
    const sat = nextDayOfWeek(6, today);
    return { date: isoDate(sat), label: 'weekend' };
  }
  // za N dni / za tydzień
  const inDays = lower.match(/\bza\s+(\d+)\s+dn[iyaiu]/);
  if (inDays) {
    const n = parseInt(inDays[1], 10);
    const t = new Date(today); t.setDate(t.getDate() + n);
    return { date: isoDate(t), label: `za ${n} dni` };
  }
  if (/\bza\s+(tydzie[ńn]|tygodnie?)\b/.test(lower)) {
    const t = new Date(today); t.setDate(t.getDate() + 7);
    return { date: isoDate(t), label: 'za tydzień' };
  }
  // dzień tygodnia (w sobotę / sobota)
  for (const [name, dow] of Object.entries(DAY_NAMES)) {
    const re = new RegExp(`\\b(w\\s+)?${name}\\b`, 'i');
    if (re.test(lower)) {
      const target = nextDayOfWeek(dow, today);
      return { date: isoDate(target), label: name };
    }
  }
  return null;
}

function parseType(input) {
  for (const { type, re, label } of TYPE_PATTERNS) {
    if (re.test(input)) return { type, label };
  }
  return { type: 'custom', label: 'Zadanie' };
}

function parsePlant(input, beds) {
  if (!Array.isArray(beds) || beds.length === 0) return null;
  const lower = input.toLowerCase();
  // Najdłuższe nazwy najpierw (żeby "pomidor malinowy" wygrał z "pomidor")
  const sorted = [...beds]
    .filter(b => b.plant_name)
    .sort((a, b) => (b.plant_name?.length || 0) - (a.plant_name?.length || 0));
  for (const bed of sorted) {
    const plantLower = bed.plant_name.toLowerCase();
    // Match "pomidor"/"pomidory"/"pomidorów" - zacznij od stem (4 znaki)
    const stem = plantLower.slice(0, Math.min(5, plantLower.length));
    const re = new RegExp(`\\b${stem}\\w*\\b`, 'i');
    if (re.test(lower)) {
      return {
        bed_id: bed.id,
        plant_name: bed.plant_name,
        plant_variety: bed.plant_variety,
        plot_name: bed.plot_name,
      };
    }
  }
  return null;
}

/**
 * Parse input string into task structure.
 * @param {string} input - tekst od usera
 * @param {Array} beds - lista bedów (z plant_name) do dopasowania rośliny
 * @returns {object} { task_type, due_date, bed_id, description, parsed }
 */
export function parseTaskInput(input, beds = []) {
  const trimmed = (input || '').trim();
  if (!trimmed) {
    return {
      task_type: 'custom',
      description: '',
      due_date: null,
      bed_id: null,
      parsed: { hasType: false, hasDate: false, hasPlant: false, typeLabel: null, dateLabel: null, plantLabel: null },
    };
  }

  const { type, label: typeLabel } = parseType(trimmed);
  const dateMatch = parseDate(trimmed);
  const plantMatch = parsePlant(trimmed, beds);

  return {
    task_type: type,
    description: trimmed,
    due_date: dateMatch?.date || null,
    bed_id: plantMatch?.bed_id || null,
    parsed: {
      hasType: type !== 'custom',
      hasDate: !!dateMatch,
      hasPlant: !!plantMatch,
      typeLabel,
      dateLabel: dateMatch?.label || null,
      plantLabel: plantMatch ? plantMatch.plant_name : null,
    },
  };
}
